import type {
  Product,
  ProductSummary,
  Blog,
  BlogSummary,
  Review,
  ShippingRule,
  Coupon,
  ApiResponse,
  SiteSettings,
  StorePromotionsPayload,
  StoreStaticPages,
  PublishedShippingRules,
  ShippingLocationMatch,
  Order,
  ReturnExchangePolicy,
  ReturnExchangeMedia,
  ReturnExchangeRequest,
  PublicPaymentConfig,
  User,
  Address,
} from '@vastra/types';
import {
  DEFAULT_SHIPPING_RULES,
  DEFAULT_STORE_PROMOTIONS,
  DEFAULT_SHIPPING_LOCATION_MATCH,
  SITE_CONFIG,
  STATIC_PAGES_DEFAULT,
} from '@vastra/constants';
import { normalizeStaticPageHtmlWhitespace } from '@vastra/utils';

const IS_PROD = process.env.NODE_ENV === 'production';
const API_TIMEOUT_MS = 15_000;

function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const signal = AbortSignal.timeout(API_TIMEOUT_MS);
  return globalThis.fetch(input, { ...init, signal: init?.signal ?? signal });
}

function parseCustomerApiBody(text: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(text) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

function messageFromCustomerApiBody(body: Record<string, unknown> | null, fallback: string): string {
  if (!body) return fallback;
  const e = body['error'];
  if (typeof e === 'string' && e.trim()) {
    const msg = e.trim();
    if (msg === 'Internal server error') return 'Something went wrong. Please try again.';
    return msg;
  }
  return fallback;
}

/**
 * Parse `{ success, error, data }` responses. Fails when HTTP is not OK, when `success === false`,
 * or when the body is not a JSON object (some proxies incorrectly return 2xx with an error payload).
 */
async function readCustomerApiEnvelope(res: Response, fallbackMessage: string): Promise<Record<string, unknown>> {
  const text = await res.text();
  const body = parseCustomerApiBody(text);
  const httpFail = !res.ok;
  const logicalFail = body !== null && body['success'] === false;
  if (httpFail || logicalFail) {
    throw new Error(messageFromCustomerApiBody(body, fallbackMessage));
  }
  if (!body) {
    throw new Error(fallbackMessage);
  }
  return body;
}

function customerApiBase(): string {
  // Use process.env.REACT_APP_* without optional chaining on `env` so Webpack DefinePlugin inlines values in production.
  const raw = process.env.REACT_APP_API_URL;
  return raw ? String(raw).replace(/\/$/, '') : '';
}
/** In dev: origin for catalog/product JSON. Empty = use relative /productdata (dev server proxies to www.stylewithvikashini.com). Set REACT_APP_STORE_URL to override. */
const DATA_BASE_URL = (() => {
  const raw = process.env.REACT_APP_STORE_URL;
  if (raw) return String(raw).replace(/\/$/, '');
  return '';
})();

let cachedCatalog: ProductSummary[] | null = null;
/** After checkout, next catalog fetch bypasses browser HTTP cache so stock matches Dynamo/S3. */
let catalogHttpCache: RequestCache = 'default';

/** Resolve productdata URLs: same-origin path in production; when DATA_BASE_URL set (dev), path-only URLs become absolute so images load. */
function toSameOriginUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('/')) {
    if (DATA_BASE_URL) return DATA_BASE_URL + url;
    return url;
  }
  try {
    const u = new URL(url);
    if (u.pathname.includes('/productdata/') && IS_PROD) return u.pathname + u.search;
  } catch {
    // ignore
  }
  return url;
}

function normalizeProductSummary(p: ProductSummary): ProductSummary {
  if (!p?.images?.length) return p;
  return {
    ...p,
    ...(p.listingUrl && { listingUrl: toSameOriginUrl(p.listingUrl) }),
    ...(p.thumbnailUrl && { thumbnailUrl: toSameOriginUrl(p.thumbnailUrl) }),
    images: p.images.map((img) => ({ ...img, url: toSameOriginUrl(img.url) })) as ProductSummary['images'],
  };
}

function normalizeProduct(p: Product): Product {
  if (!p?.images?.length) return p;
  return {
    ...p,
    images: p.images.map((img) => ({
      ...img,
      url: toSameOriginUrl(img.url),
      ...(img.listingUrl && { listingUrl: toSameOriginUrl(img.listingUrl) }),
      ...(img.thumbnailUrl && { thumbnailUrl: toSameOriginUrl(img.thumbnailUrl) }),
      ...(img.avifUrl && { avifUrl: toSameOriginUrl(img.avifUrl) }),
      ...(img.avifListingUrl && { avifListingUrl: toSameOriginUrl(img.avifListingUrl) }),
      ...(img.avifThumbnailUrl && { avifThumbnailUrl: toSameOriginUrl(img.avifThumbnailUrl) }),
    })),
  };
}

/** Storefront must not show drafts, soft-deleted, or stale CDN copies of unpublished products. */
function isPublicCatalogSummary(p: ProductSummary): boolean {
  if (p.isSoftDeleted === true) return false;
  if (p.isPublished === false) return false;
  return true;
}

function isPublicProductDetail(p: Product): boolean {
  if (p.isSoftDeleted === true) return false;
  if (p.isPublished === false) return false;
  return true;
}
let cachedBlogCatalog: BlogSummary[] | null = null;

/** Call after a successful payment so the next listing load reflects decremented stock. */
export function clearProductCatalogCache(): void {
  cachedCatalog = null;
  catalogHttpCache = 'no-store';
}

/** Load product listing from catalog.json (metadata + links). Small file for listing. */
async function loadCatalog(): Promise<ProductSummary[]> {
  if (cachedCatalog) return cachedCatalog;

  const catalogUrl = IS_PROD || !DATA_BASE_URL ? '/productdata/catalog.json' : `${DATA_BASE_URL}/productdata/catalog.json`;
  if (catalogUrl) {
    try {
      const res = await fetchWithTimeout(catalogUrl, { cache: catalogHttpCache });
      catalogHttpCache = 'default';
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        console.warn(`[Catalog] Fetch failed: ${res.status} ${res.statusText} for ${catalogUrl}. Run upload-data and invalidate CloudFront /data/*.`);
        throw new Error(`${res.status}`);
      }
      // CloudFront may return index.html (200) for missing /data/* when 404->index.html is configured
      if (contentType.includes('text/html')) {
        console.warn('[Catalog] Response was HTML instead of JSON. catalog.json may be missing in S3. Run: npm run upload-data, then invalidate CloudFront /data/*.');
        throw new Error('HTML response');
      }
      const data = await res.json();
      if (!Array.isArray(data.products)) {
        console.warn('[Catalog] Invalid catalog shape (missing products array).');
        throw new Error('Invalid shape');
      }
      cachedCatalog = (data.products as ProductSummary[])
        .map(normalizeProductSummary)
        .filter(isPublicCatalogSummary);
      return cachedCatalog;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[Catalog] Load failed:', e instanceof Error ? e.message : e);
      }
    }
    if (IS_PROD) return [];
  }

  if (!IS_PROD) {
    const { mockProducts } = await import('../data/mockProducts');
    cachedCatalog = (mockProducts as unknown as ProductSummary[])
      .map(normalizeProductSummary)
      .filter(isPublicCatalogSummary);
    return cachedCatalog;
  }
  return [];
}

/** Load full product details from products/{id}.json. Fetched on demand for detail page. */
async function loadProductDetail(id: string): Promise<Product | null> {
  const url = IS_PROD || !DATA_BASE_URL ? `/productdata/data/products/${id}.json` : `${DATA_BASE_URL}/productdata/data/products/${id}.json`;
  if (url) {
    try {
      const res = await fetchWithTimeout(url);
      if (res.ok) {
        const product = (await res.json()) as Product;
        if (!isPublicProductDetail(product)) return null;
        return normalizeProduct(product);
      }
    } catch {}
  }
  if (!IS_PROD) {
    const { mockProducts } = await import('../data/mockProducts');
    const product = mockProducts.find((p) => p.id === id) ?? null;
    if (product && !isPublicProductDetail(product)) return null;
    return product;
  }
  return null;
}

/** Load blog catalog (metadata for listing) from blogs.json. */
async function loadBlogCatalog(): Promise<BlogSummary[]> {
  if (cachedBlogCatalog) return cachedBlogCatalog;

  if (IS_PROD) {
    try {
      const res = await fetchWithTimeout('/productdata/data/blogs.json');
      if (res.ok) {
        const data = await res.json();
        const blogs = data.blogs ?? [];
        cachedBlogCatalog = blogs;
        return blogs;
      }
    } catch {}
    return [];
  }

  const { mockBlogs } = await import('../data/mockBlogs');
  cachedBlogCatalog = mockBlogs as BlogSummary[];
  return cachedBlogCatalog;
}

/** Load full blog by slug from blogs/{slug}.json. */
async function loadBlogDetail(slug: string): Promise<Blog | null> {
  if (IS_PROD) {
    try {
      const res = await fetchWithTimeout(`/productdata/data/blogs/${slug}.json`);
      if (res.ok) return await res.json();
    } catch {}
    return null;
  }

  const { mockBlogs } = await import('../data/mockBlogs');
  return mockBlogs.find((b) => b.slug === slug) ?? null;
}

let cachedSiteSettings: SiteSettings | null = null;
let cachedPromotions: StorePromotionsPayload | null = null;
let cachedPaymentConfig: PublicPaymentConfig | null | undefined;
let paymentConfigPromise: Promise<PublicPaymentConfig | null> | null = null;
/** Cached published shipping config from `shipping-rules.json`; undefined = not fetched yet. */
let publishedShippingConfigCache: PublishedShippingRules | null | undefined;

async function loadPublishedShippingConfig(): Promise<PublishedShippingRules | null> {
  if (publishedShippingConfigCache !== undefined) {
    return publishedShippingConfigCache;
  }

  const jsonUrl =
    IS_PROD || !DATA_BASE_URL
      ? '/productdata/data/shipping-rules.json'
      : `${DATA_BASE_URL}/productdata/data/shipping-rules.json`;

  try {
    const res = await fetchWithTimeout(jsonUrl);
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('text/html')) {
        const body = (await res.json()) as { rules?: ShippingRule[]; locationMatch?: string; updatedAt?: string };
        const rules = body.rules;
        if (Array.isArray(rules) && rules.length > 0) {
          const active = rules.filter((r) => r.isActive !== false);
          const list = active.length ? active : rules;
          const locationMatch: ShippingLocationMatch =
            body.locationMatch === 'pincode' ? 'pincode' : DEFAULT_SHIPPING_LOCATION_MATCH;
          publishedShippingConfigCache = { rules: list, locationMatch, updatedAt: body.updatedAt };
          return publishedShippingConfigCache;
        }
      }
    }
  } catch {
    // treat as unpublished
  }

  publishedShippingConfigCache = null;
  return null;
}

async function loadPublishedShippingRulesFromFile(): Promise<ShippingRule[] | null> {
  const c = await loadPublishedShippingConfig();
  return c?.rules?.length ? c.rules : null;
}

function mergePromotionsPayload(raw: Partial<StorePromotionsPayload> | null | undefined): StorePromotionsPayload {
  const base = DEFAULT_STORE_PROMOTIONS;
  const bannerBase = base.banner ?? { enabled: false, message: '', variant: 'amber' as const };
  const heroBase = base.hero ?? {
    enabled: true,
    mode: 'brand' as const,
    headline: '',
  };
  const bannersRaw =
    Array.isArray(raw?.banners) && raw.banners.length > 0
      ? raw.banners
      : raw?.banner
        ? [raw.banner]
        : base.banners?.length
          ? base.banners
          : [bannerBase];
  const banners = bannersRaw.map((b) => ({
    ...bannerBase,
    ...b,
    enabled: b?.enabled ?? bannerBase.enabled,
    message: b?.message ?? bannerBase.message,
  }));
  const banner = banners[0] ?? { ...bannerBase };
  const h = raw?.hero;
  const maxActiveBanners = raw?.maxActiveBanners === 1 ? 1 : 2;
  return {
    banner,
    banners,
    maxActiveBanners,
    hero: {
      ...heroBase,
      ...h,
      enabled: h?.enabled ?? heroBase.enabled,
      mode: h?.mode ?? heroBase.mode,
      headline: h?.headline ?? heroBase.headline,
    },
    coupons: Array.isArray(raw?.coupons) ? raw!.coupons : [],
    updatedAt: raw?.updatedAt,
  };
}

/**
 * Storefront promotions (banner, hero, coupons). Tries static JSON, then API if REACT_APP_API_URL is set.
 */
export async function loadPromotions(): Promise<StorePromotionsPayload> {
  if (cachedPromotions) return cachedPromotions;

  const tryJson = async (url: string): Promise<StorePromotionsPayload | null> => {
    try {
      const res = await fetchWithTimeout(url);
      if (!res.ok) return null;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('text/html')) return null;
      const data = (await res.json()) as Partial<StorePromotionsPayload>;
      return mergePromotionsPayload(data);
    } catch {
      return null;
    }
  };

  const jsonUrl =
    IS_PROD || !DATA_BASE_URL ? '/productdata/data/promotions.json' : `${DATA_BASE_URL}/productdata/data/promotions.json`;
  const fromStatic = await tryJson(jsonUrl);
  if (fromStatic) {
    cachedPromotions = fromStatic;
    return cachedPromotions;
  }

  const apiBase = customerApiBase();
  if (apiBase) {
    try {
      const res = await fetchWithTimeout(`${apiBase}/api/config/promotions`);
      if (res.ok) {
        const body = (await res.json()) as { data?: Partial<StorePromotionsPayload> };
        if (body.data) {
          cachedPromotions = mergePromotionsPayload(body.data);
          return cachedPromotions;
        }
      }
    } catch {
      // ignore
    }
  }

  if (!IS_PROD) {
    cachedPromotions = mergePromotionsPayload({});
    return cachedPromotions;
  }

  cachedPromotions = mergePromotionsPayload({});
  return cachedPromotions;
}

/** Load store/contact/social settings from JSON (same origin or DATA_BASE_URL). Falls back to SITE_CONFIG. */
function mapSiteSettingsFromResponse(d: SiteSettings): SiteSettings {
  return {
    businessName: d.businessName,
    address: d.address,
    contactPhone: d.contactPhone,
    contactWhatsApp: d.contactWhatsApp,
    contactEmail: d.contactEmail,
    socialMedia: d.socialMedia,
    pricesIncludeAllTaxes: d.pricesIncludeAllTaxes,
    gstRatePercentIncluded: d.gstRatePercentIncluded,
    reviewsEnabled: d.reviewsEnabled,
    multiLanguageEnabled: d.multiLanguageEnabled,
    additionalStorefrontLocales: d.additionalStorefrontLocales,
  };
}

export async function getSiteSettings(): Promise<SiteSettings> {
  if (cachedSiteSettings) return cachedSiteSettings;
  const url = IS_PROD || !DATA_BASE_URL ? '/productdata/data/site-settings.json' : `${DATA_BASE_URL}/productdata/data/site-settings.json`;
  if (url) {
    try {
      const res = await fetchWithTimeout(url);
      if (res.ok) {
        const text = await res.text();
        // CloudFront SPA custom errors can return 200 + index.html for a missing S3 object under /productdata/*.
        if (!text.trimStart().startsWith('<')) {
          try {
            const data = JSON.parse(text) as SiteSettings;
            cachedSiteSettings = mapSiteSettingsFromResponse(data);
            return cachedSiteSettings;
          } catch {
            // not JSON
          }
        }
      }
    } catch {}
  }

  const apiBase = customerApiBase();
  if (apiBase) {
    try {
      const res = await fetchWithTimeout(`${apiBase}/api/config/site-settings`);
      if (res.ok) {
        const body = (await res.json()) as { data?: SiteSettings };
        if (body.data) {
          cachedSiteSettings = mapSiteSettingsFromResponse(body.data);
          return cachedSiteSettings;
        }
      }
    } catch {
      // ignore
    }
  }

  return {
    businessName: SITE_CONFIG.siteName,
    contactPhone: SITE_CONFIG.contactPhone,
    contactWhatsApp: SITE_CONFIG.contactWhatsApp,
    contactEmail: SITE_CONFIG.contactEmail,
    socialMedia: {
      facebook: SITE_CONFIG.socialLinks?.facebook,
      instagram: SITE_CONFIG.socialLinks?.instagram,
      youtube: SITE_CONFIG.socialLinks?.youtube,
    },
    pricesIncludeAllTaxes: true,
    gstRatePercentIncluded: 5,
    reviewsEnabled: true,
  };
}

let cachedStoreStaticPages: StoreStaticPages | undefined;

function parseStoreStaticPages(raw: unknown): StoreStaticPages | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.version !== 1) return null;
  const ship = o.shipping as Record<string, unknown> | undefined;
  const terms = o.terms as Record<string, unknown> | undefined;
  const priv = o.privacy as Record<string, unknown> | undefined;
  if (!ship || !terms || !priv) return null;
  const need = (s: Record<string, unknown>, keys: string[]) => keys.every((k) => typeof s[k] === 'string');
  if (
    !need(ship, [
      'pageTitle',
      'introWhenRatesPublishedHtml',
      'introWhenRatesLoadingHtml',
      'introWhenRatesComingSoonHtml',
      'ratesFootnoteHtml',
      'orderProcessingHtml',
      'returnsHtml',
      'helpHtml',
    ])
  )
    return null;
  if (!need(terms, ['pageTitle', 'lastUpdatedHtml', 'bodyHtml'])) return null;
  if (!need(priv, ['pageTitle', 'lastUpdatedHtml', 'bodyHtml'])) return null;
  return o as unknown as StoreStaticPages;
}

/** Safe text for injecting into HTML: no raw tags/newlines that would break layout vs admin preview. */
function staticPageTokenText(s: string): string {
  const oneLine = s.trim().replace(/\s+/g, ' ');
  return oneLine
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Multi-line business address: escape each line, join with &lt;br&gt; for use inside HTML blocks. */
function staticPageAddressHtml(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  return raw
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => staticPageTokenText(line))
    .join('<br>');
}

export interface ApplyStaticPageHtmlOptions {
  /** From site settings (`site-settings.json` → `address`). */
  contactAddress?: string;
  /** From site settings (`site-settings.json` → `contactPhone`). */
  contactPhone?: string;
  /** From return-exchange-policy.json (public). */
  returnExchangePolicy?: Pick<
    ReturnExchangePolicy,
    'defaultReturnWindowDays' | 'shippingRefundPolicy' | 'discountAllocationPolicy' | 'returnsEnabled' | 'exchangesEnabled'
  >;
}

/** Replace tokens used in admin-authored HTML. */
export function applyStaticPageHtml(html: string, opts?: ApplyStaticPageHtmlOptions): string {
  const wa = SITE_CONFIG.contactWhatsApp.replace(/[^0-9]/g, '');
  const normalized = normalizeStaticPageHtmlWhitespace(html);
  const phoneDisplay = (opts?.contactPhone?.trim() || SITE_CONFIG.contactPhone || '').trim();
  const rex = opts?.returnExchangePolicy;
  return normalized
    .replace(/\{\{SITE_NAME\}\}/g, staticPageTokenText(SITE_CONFIG.siteName))
    .replace(/\{\{CONTACT_EMAIL\}\}/g, staticPageTokenText(SITE_CONFIG.contactEmail))
    .replace(/\{\{CONTACT_PHONE\}\}/g, staticPageTokenText(phoneDisplay))
    .replace(/\{\{CONTACT_ADDRESS\}\}/g, staticPageAddressHtml(opts?.contactAddress))
    .replace(/\{\{DEFAULT_RETURN_WINDOW_DAYS\}\}/g, staticPageTokenText(String(rex?.defaultReturnWindowDays ?? '')))
    .replace(/\{\{RETURNS_ENABLED\}\}/g, staticPageTokenText(String(rex?.returnsEnabled === true)))
    .replace(/\{\{EXCHANGES_ENABLED\}\}/g, staticPageTokenText(String(rex?.exchangesEnabled === true)))
    .replace(/\{\{SHIPPING_REFUND_POLICY\}\}/g, staticPageTokenText(String(rex?.shippingRefundPolicy ?? '')))
    .replace(/\{\{DISCOUNT_ALLOCATION_POLICY\}\}/g, staticPageTokenText(String(rex?.discountAllocationPolicy ?? '')))
    .replace(/\{\{WHATSAPP_HREF\}\}/g, `https://wa.me/${wa}`);
}

let cachedReturnExchangePolicy: ReturnExchangePolicy | null | undefined = undefined;

/** Public policy JSON from `productdata/data/return-exchange-policy.json` (cached). */
export async function loadReturnExchangePolicyPublic(): Promise<ReturnExchangePolicy | null> {
  if (cachedReturnExchangePolicy !== undefined) return cachedReturnExchangePolicy;
  const jsonUrl =
    IS_PROD || !DATA_BASE_URL
      ? '/productdata/data/return-exchange-policy.json'
      : `${DATA_BASE_URL}/productdata/data/return-exchange-policy.json`;
  try {
    const res = await fetchWithTimeout(jsonUrl);
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('text/html')) {
        const raw = (await res.json()) as ReturnExchangePolicy;
        cachedReturnExchangePolicy = raw ?? null;
        return cachedReturnExchangePolicy;
      }
    }
  } catch {
    /* ignore */
  }
  cachedReturnExchangePolicy = null;
  return null;
}

/** Legal / info pages from `productdata/data/static-pages.json`, or built-in defaults if missing. */
export async function loadStoreStaticPages(): Promise<StoreStaticPages> {
  if (cachedStoreStaticPages !== undefined) {
    return cachedStoreStaticPages;
  }

  const jsonUrl =
    IS_PROD || !DATA_BASE_URL
      ? '/productdata/data/static-pages.json'
      : `${DATA_BASE_URL}/productdata/data/static-pages.json`;

  try {
    const res = await fetchWithTimeout(jsonUrl);
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('text/html')) {
        const raw = await res.json();
        const parsed = parseStoreStaticPages(raw);
        if (parsed) {
          cachedStoreStaticPages = parsed;
          return parsed;
        }
      }
    }
  } catch {
    /* use defaults */
  }

  cachedStoreStaticPages = STATIC_PAGES_DEFAULT;
  return STATIC_PAGES_DEFAULT;
}

export const api = {
  async getProducts(): Promise<ApiResponse<ProductSummary[]>> {
    const products = await loadCatalog();
    return { success: true, data: products };
  },

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    const product = await loadProductDetail(id);
    if (!product) return { success: false, error: 'Product not found' };
    return { success: true, data: product };
  },

  async getBlogs(): Promise<ApiResponse<BlogSummary[]>> {
    const catalog = await loadBlogCatalog();
    return { success: true, data: catalog.filter((b) => b.isPublished) };
  },

  async getBlog(slug: string): Promise<ApiResponse<Blog>> {
    const blog = await loadBlogDetail(slug);
    if (!blog) return { success: false, error: 'Blog not found' };
    return { success: true, data: blog };
  },

  async getReviews(productId: string): Promise<ApiResponse<Review[]>> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const qs = new URLSearchParams({ productId });
    const res = await fetchWithTimeout(`${base}/api/reviews?${qs}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await readCustomerApiEnvelope(res, 'Could not load reviews');
    const data = body['data'];
    return Array.isArray(data) ? { success: true, data: data as Review[] } : { success: true, data: [] };
  },

  async postReview(payload: { productId: string; rating: number; title?: string; comment: string; userName?: string }, token: string): Promise<ApiResponse<Review>> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    const body = await readCustomerApiEnvelope(res, 'Could not submit review');
    const data = body['data'] as Review | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'Could not submit review'));
    return { success: true, data };
  },

  /** Published zones from `shipping-rules.json` only, or `null` if unpublished — used by the /shipping page. */
  getPublishedShippingRules: () => loadPublishedShippingRulesFromFile(),

  /** Full published payload (`rules` + `locationMatch`) or `null` if the file is missing — for /shipping zone details. */
  getPublishedShippingConfig: () => loadPublishedShippingConfig(),

  /**
   * Cart/checkout: published `shipping-rules.json` + `locationMatch`, or built-in defaults.
   */
  async getShippingCheckoutConfig(): Promise<PublishedShippingRules> {
    const fromFile = await loadPublishedShippingConfig();
    if (fromFile && fromFile.rules.length > 0) return fromFile;
    return {
      rules: DEFAULT_SHIPPING_RULES,
      locationMatch: DEFAULT_SHIPPING_LOCATION_MATCH,
    };
  },

  /**
   * Rules only (no location mode). Prefer getShippingCheckoutConfig for cart.
   */
  async getShippingRules(): Promise<ApiResponse<ShippingRule[]>> {
    const cfg = await loadPublishedShippingConfig();
    const rules = cfg?.rules?.length ? cfg.rules : DEFAULT_SHIPPING_RULES;
    return { success: true, data: rules };
  },

  async getCoupons(): Promise<ApiResponse<Coupon[]>> {
    const p = await loadPromotions();
    const coupons = Array.isArray(p.coupons) ? p.coupons : [];
    return { success: true, data: coupons };
  },

  loadPromotions,

  /**
   * Legacy: reads `vastra_orders` from localStorage only. The app uses `OrdersProvider` + `fetchCustomerOrders` instead.
   */
  async getOrders(): Promise<ApiResponse<Order[]>> {
    const stored = localStorage.getItem('vastra_orders');
    let orders: Order[] = [];
    if (stored) {
      try { orders = JSON.parse(stored); } catch { /* corrupt localStorage entry */ }
    }
    return { success: true, data: orders };
  },

  /** Hydrate order on thank-you page when `paymentClientSecret` is still in sessionStorage (same browser tab). */
  async fetchOrderByPaymentSession(orderId: string, paymentClientSecret: string): Promise<Order> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/public/order-by-payment-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, paymentClientSecret }),
    });
    const body = await readCustomerApiEnvelope(res, 'Could not load order');
    const data = body['data'] as Order | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'Could not load order'));
    return data;
  },

  /** Authenticated customer orders (Cognito JWT). Merged in OrdersProvider with device-local orders. */
  async fetchCustomerOrders(token: string, outcome: string = 'successful'): Promise<Order[]> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const qs = new URLSearchParams({ limit: '100', outcome });
    const res = await fetchWithTimeout(`${base}/api/orders?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await readCustomerApiEnvelope(res, 'Could not load orders');
    const data = body['data'];
    return Array.isArray(data) ? (data as Order[]) : [];
  },

  async fetchCustomerOrder(orderId: string, token: string): Promise<Order> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/orders/${encodeURIComponent(orderId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await readCustomerApiEnvelope(res, 'Order not found');
    const data = body['data'] as Order | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'Order not found'));
    return data;
  },

  async fetchCustomerMe(token: string): Promise<User> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await readCustomerApiEnvelope(res, 'Could not load profile');
    const data = body['data'] as User | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'Could not load profile'));
    return data;
  },

  async putCustomerProfile(token: string, body: { name?: string; phone?: string }): Promise<User> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/auth/me`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Could not update profile');
    const data = env['data'] as User | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Could not update profile'));
    return data;
  },

  async putCustomerAddresses(token: string, addresses: Address[]): Promise<User> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/auth/me/addresses`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ addresses }),
    });
    const env = await readCustomerApiEnvelope(res, 'Could not save addresses');
    const data = env['data'] as User | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Could not save addresses'));
    return data;
  },

  customerApiBase,

  async getPublicPaymentConfig(): Promise<PublicPaymentConfig | null> {
    if (cachedPaymentConfig !== undefined) return cachedPaymentConfig;
    if (paymentConfigPromise) return paymentConfigPromise;
    const base = customerApiBase();
    if (!base) return null;
    paymentConfigPromise = (async () => {
      try {
        const res = await fetchWithTimeout(`${base}/api/public/payment-config`);
        if (!res.ok) {
          return null;
        }
        const json = (await res.json()) as { data?: PublicPaymentConfig };
        const data = json.data ?? null;
        cachedPaymentConfig = data;
        return data;
      } catch {
        return null;
      } finally {
        paymentConfigPromise = null;
      }
    })();
    return paymentConfigPromise;
  },

  async createCheckoutOrder(
    payload: Record<string, unknown>,
    opts?: { token?: string | null }
  ): Promise<{ order: Order; paymentClientSecret: string }> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (opts?.token) headers['Authorization'] = `Bearer ${opts.token}`;
    const res = await fetchWithTimeout(`${base}/api/checkout/create-order`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    const body = await readCustomerApiEnvelope(res, 'Could not create order');
    const data = body['data'] as { order?: Order; paymentClientSecret?: string } | undefined;
    if (!data?.order || !data.paymentClientSecret) {
      throw new Error(messageFromCustomerApiBody(body, 'Invalid checkout response'));
    }
    return { order: data.order, paymentClientSecret: data.paymentClientSecret };
  },

  async initiatePaytmCheckout(body: { orderId: string; paymentClientSecret: string }): Promise<Record<string, unknown>> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/checkout/paytm/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Could not start payment');
    const data = env['data'] as Record<string, unknown> | undefined;
    return data ?? {};
  },

  async simulatePaytmComplete(body: { orderId: string; paymentClientSecret: string }): Promise<Order> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/checkout/paytm/simulate-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Payment completion failed');
    const wrap = env['data'] as { order?: Order } | undefined;
    if (!wrap?.order) throw new Error(messageFromCustomerApiBody(env, 'Invalid payment response'));
    return wrap.order;
  },

  async lookupOrderStatus(payload: {
    orderId: string;
    email?: string;
    phone?: string;
    captchaToken?: string;
  }): Promise<Order> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/public/order-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await readCustomerApiEnvelope(res, 'Lookup failed');
    const data = body['data'] as Order | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'No data'));
    return data;
  },

  async getCustomerLoginChallenge(email: string): Promise<{ captchaRequired: boolean; failures: number }> {
    const base = customerApiBase();
    if (!base) return { captchaRequired: false, failures: 0 };
    try {
      const res = await fetchWithTimeout(
        `${base}/api/auth/login-challenge?email=${encodeURIComponent(email)}&type=customer`
      );
      const json = (await res.json()) as { data?: { captchaRequired?: boolean; failures?: number } };
      return {
        captchaRequired: Boolean(json.data?.captchaRequired),
        failures: Number(json.data?.failures) || 0,
      };
    } catch {
      return { captchaRequired: false, failures: 0 };
    }
  },

  async getReturnExchangeEligibility(
    orderId: string,
    token: string,
  ): Promise<{
    orderId: string;
    lines: Array<{
      productId: string;
      name: string;
      maxQty: number;
      eligible: boolean;
      daysRemaining: number;
      returnsEnabled: boolean;
      exchangesEnabled: boolean;
      exchangeCountExceeded?: boolean;
    }>;
    policy: {
      returnsEnabled: boolean;
      exchangesEnabled: boolean;
      approvalRequiredForReturn: boolean;
      approvalRequiredForExchange: boolean;
    };
  }> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(
      `${base}/api/orders/${encodeURIComponent(orderId)}/return-exchange/eligibility`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const body = await readCustomerApiEnvelope(res, 'Could not load eligibility');
    const data = body['data'] as
      | {
          orderId: string;
          lines: Array<{
            productId: string;
            name: string;
            maxQty: number;
            eligible: boolean;
            daysRemaining: number;
            returnsEnabled: boolean;
            exchangesEnabled: boolean;
            exchangeCountExceeded?: boolean;
          }>;
          policy: {
            returnsEnabled: boolean;
            exchangesEnabled: boolean;
            approvalRequiredForReturn: boolean;
            approvalRequiredForExchange: boolean;
          };
        }
      | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(body, 'Invalid eligibility response'));
    return data;
  },

  async createReturnExchangeDraft(
    token: string,
    orderId: string,
    body: { kind: 'return' | 'exchange'; items: { productId: string; quantity: number }[]; reasonText?: string },
  ): Promise<ReturnExchangeRequest> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/orders/${encodeURIComponent(orderId)}/return-exchange`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Could not create request');
    const data = env['data'] as ReturnExchangeRequest | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Invalid response'));
    return data;
  },

  async getReturnExchangeRequest(requestId: string, token: string): Promise<ReturnExchangeRequest> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/return-exchange/${encodeURIComponent(requestId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const env = await readCustomerApiEnvelope(res, 'Not found');
    const data = env['data'] as ReturnExchangeRequest | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Not found'));
    return data;
  },

  async submitReturnExchangeRequest(
    token: string,
    requestId: string,
    body: { reasonText?: string; media?: ReturnExchangeMedia[] },
  ): Promise<ReturnExchangeRequest> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/return-exchange/${encodeURIComponent(requestId)}/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Submit failed');
    const data = env['data'] as ReturnExchangeRequest | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Invalid response'));
    return data;
  },

  async updateReturnExchangeReturnShipment(
    token: string,
    requestId: string,
    body: { courierCompany?: string; trackingNumber?: string; trackingUrl?: string },
  ): Promise<ReturnExchangeRequest> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(
      `${base}/api/return-exchange/${encodeURIComponent(requestId)}/return-shipment`,
      {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    const env = await readCustomerApiEnvelope(res, 'Could not save tracking');
    const data = env['data'] as ReturnExchangeRequest | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Invalid response'));
    return data;
  },

  async cancelReturnExchangeRequest(token: string, requestId: string): Promise<ReturnExchangeRequest> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/return-exchange/${encodeURIComponent(requestId)}/cancel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const env = await readCustomerApiEnvelope(res, 'Cancel failed');
    const data = env['data'] as ReturnExchangeRequest | undefined;
    if (!data) throw new Error(messageFromCustomerApiBody(env, 'Invalid response'));
    return data;
  },

  async presignReturnExchangeMedia(
    token: string,
    body: { requestId: string; contentType: string; fileSize?: number },
  ): Promise<{ uploadUrl: string; key: string; mediaId: string; publicUrl: string; type: 'image' | 'video' }> {
    const base = customerApiBase();
    if (!base) throw new Error('REACT_APP_API_URL is not set');
    const res = await fetchWithTimeout(`${base}/api/return-exchange/media/presign`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const env = await readCustomerApiEnvelope(res, 'Presign failed');
    const data = env['data'] as
      | { uploadUrl: string; key: string; mediaId: string; publicUrl: string; type: 'image' | 'video' }
      | undefined;
    if (!data?.uploadUrl || !data.mediaId) throw new Error(messageFromCustomerApiBody(env, 'Invalid presign response'));
    return data;
  },

  async postCustomerLoginAttempt(payload: {
    email: string;
    outcome: 'success' | 'failure';
    captchaToken?: string;
  }): Promise<{ ok?: boolean; failures?: number; captchaRequired?: boolean }> {
    const base = customerApiBase();
    if (!base) return { ok: true };
    const res = await fetchWithTimeout(`${base}/api/auth/customer-login-attempt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const env = await readCustomerApiEnvelope(res, 'Request failed');
    const data = env['data'] as { ok?: boolean; failures?: number; captchaRequired?: boolean } | undefined;
    return data ?? { ok: true };
  },

  /** Store/contact/social from JSON (configured in admin). Used by Footer. */
  getSiteSettings,

  loadStoreStaticPages,

  applyStaticPageHtml,
};
